import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import {
  IconPlus,
  IconTrash,
  IconRefresh,
  IconClose,
  IconEdit,
} from "../icons";
import ErrorState from "./ErrorState";
import { dataBr } from "./PainelView";

/**
 * ATUALIZAÇÕES — o que foi entregue, e o aviso disso dentro dos sistemas.
 *
 * O botão "Avisar nos sistemas" publica a faixa que já existe hoje em todos os
 * sistemas (a mesma de manutenção/comunicado). Ela sai sozinha depois dos dias
 * escolhidos, e dá para tirar do ar antes disso.
 */

const VAZIO = { titulo: "", descricao: "", sistema: "", versao: "", data: "", status: "Publicada" };

export default function AtualizacoesView({ onMudou }: { onMudou: () => void }) {
  const [linhas, setLinhas] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<any | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [avisando, setAvisando] = useState<any | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLinhas((await api.atualizacoes.listar()) as any[]);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar as atualizações.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar() {
    if (!form?.titulo?.trim()) {
      alert("Diga o que foi atualizado.");
      return;
    }
    setSalvando(true);
    try {
      if (form.id) await api.atualizacoes.atualizar(form.id, form);
      else await api.atualizacoes.criar(form);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  async function desavisar(a: any) {
    if (!confirm("Tirar a faixa de aviso do ar?")) return;
    try {
      const r = await api.atualizacoes.desavisar(a.id);
      alert(r?.mensagem ?? "Aviso encerrado.");
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao encerrar o aviso.");
    }
  }

  async function excluir(a: any) {
    if (!confirm("Apagar o registro " + a.titulo + "?")) return;
    try {
      await api.atualizacoes.remover(a.id);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  const set = (campo: string) => (e: any) => setForm({ ...form, [campo]: e.target.value });

  return (
    <>
      <div className="crud-toolbar">
        <p className="muted">
          Cada entrega registrada aqui vira o histórico do sistema — e o aviso que aparece dentro
          dele para quem usa.
        </p>
        <div className="toolbar-right">
          <button className="btn-primary" onClick={() => setForm({ ...VAZIO })}>
            <IconPlus size={16} /> Registrar atualização
          </button>
        </div>
      </div>

      {form && (
        <div className="form-card">
          <div className="form-grid">
            <label className="field field-wide">
              <span>
                O que foi atualizado <span className="req">*</span>
              </span>
              <input value={form.titulo} onChange={set("titulo")} autoFocus />
            </label>
            <label className="field field-wide">
              <span>Detalhes (o que muda para quem usa)</span>
              <textarea rows={3} value={form.descricao ?? ""} onChange={set("descricao")} />
            </label>
            <label className="field">
              <span>Sistema</span>
              <select value={form.sistema ?? ""} onChange={set("sistema")}>
                <option value="">Todos / geral</option>
                {SISTEMAS_EM_ORDEM.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Versão</span>
              <input value={form.versao ?? ""} onChange={set("versao")} placeholder="ex.: 1.4" />
            </label>
            <label className="field">
              <span>Data da entrega</span>
              <input type="date" value={form.data ?? ""} onChange={set("data")} />
            </label>
            <label className="field">
              <span>Situação</span>
              <select value={form.status ?? "Publicada"} onChange={set("status")}>
                <option>Publicada</option>
                <option>Rascunho</option>
              </select>
            </label>
          </div>
          <div className="solicitar-foot">
            <button
              className="btn-secondary btn-sm"
              onClick={() => setForm(null)}
              disabled={salvando}
            >
              Cancelar
            </button>
            <button className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : form.id ? "Salvar" : "Registrar"}
            </button>
          </div>
        </div>
      )}

      {erro ? (
        <ErrorState message={erro} onRetry={carregar} />
      ) : carregando ? (
        <div className="carregando">Carregando…</div>
      ) : linhas.length === 0 ? (
        <div className="empty">
          <IconRefresh size={30} />
          Nada registrado ainda. Ao entregar uma melhoria, registre aqui e clique em{" "}
          <strong>Avisar nos sistemas</strong> — a faixa aparece para quem usa.
        </div>
      ) : (
        <ul className="dev-lista dev-lista-cards">
          {linhas.map((a) => (
            <li key={a.id}>
              <span className="pill pill-neutral">{dataBr(a.data)}</span>
              <span className="dev-lista-main">
                <strong>{a.titulo}</strong>
                <small className="muted">
                  {nomeDoSistema(a.sistema)}
                  {a.versao ? " · versão " + a.versao : ""}
                  {a.status === "Rascunho" ? " · rascunho" : ""}
                  {a.descricao ? " · " + a.descricao : ""}
                </small>
                {a.avisado_em && (
                  <small className={a.aviso_ativo ? "dev-ok" : "muted"}>
                    {a.aviso_ativo
                      ? "Faixa no ar nos sistemas desde " + dataBr(a.avisado_em)
                      : "Aviso encerrado"}
                  </small>
                )}
              </span>
              <span className="dev-acoes">
                <button
                  className="btn-secondary btn-sm"
                  title="Publicar a faixa dentro dos sistemas"
                  onClick={() => setAvisando(a)}
                >
                  <IconRefresh size={15} /> Avisar nos sistemas
                </button>
                {a.aviso_ativo && (
                  <button className="icon-btn" title="Tirar a faixa do ar" onClick={() => desavisar(a)}>
                    <IconClose size={16} />
                  </button>
                )}
                <button
                  className="icon-btn"
                  title="Editar"
                  onClick={() => setForm({ ...a, data: String(a.data).slice(0, 10) })}
                >
                  <IconEdit size={16} />
                </button>
                <button className="icon-btn icon-btn-danger" title="Excluir" onClick={() => excluir(a)}>
                  <IconTrash size={16} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {avisando && (
        <AvisarModal
          atualizacao={avisando}
          onClose={() => setAvisando(null)}
          onEnviado={async () => {
            await carregar();
            onMudou();
          }}
        />
      )}
    </>
  );
}

/* ============ A faixa que vai aparecer DENTRO dos sistemas ============ */

function AvisarModal({
  atualizacao,
  onClose,
  onEnviado,
}: {
  atualizacao: any;
  onClose: () => void;
  onEnviado: () => Promise<void> | void;
}) {
  const [titulo, setTitulo] = useState("Sistema atualizado");
  const [mensagem, setMensagem] = useState(
    atualizacao.titulo +
      (atualizacao.descricao ? " — " + atualizacao.descricao : "") +
      (atualizacao.versao ? " (versão " + atualizacao.versao + ")" : ""),
  );
  const [alvo, setAlvo] = useState<string>(atualizacao.sistema ?? "todos");
  const [dias, setDias] = useState("7");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.atualizacoes.avisar(atualizacao.id, {
        titulo,
        mensagem,
        sistema_alvo: alvo,
        dias: Number(dias) || 7,
      });
      alert(r?.mensagem ?? "Aviso publicado.");
      await onEnviado();
      onClose();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível publicar o aviso.");
      setEnviando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Avisar nos sistemas</h3>
            <p className="modal-sub">
              A mensagem aparece como faixa no topo do sistema escolhido, para todo mundo que usa.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label className="field field-wide">
              <span>Título da faixa</span>
              <input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            </label>
            <label className="field field-wide">
              <span>Mensagem</span>
              <textarea rows={3} value={mensagem} onChange={(e) => setMensagem(e.target.value)} />
            </label>
            <label className="field">
              <span>Onde aparece</span>
              <select value={alvo} onChange={(e) => setAlvo(e.target.value)}>
                <option value="todos">Todos os sistemas</option>
                {SISTEMAS_EM_ORDEM.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Fica no ar por (dias)</span>
              <input
                type="number"
                min={1}
                max={60}
                value={dias}
                onChange={(e) => setDias(e.target.value)}
              />
            </label>
          </div>
          <p className="hint">
            Quem usa o sistema pode dispensar a faixa; ela some sozinha no prazo e você pode
            encerrá-la antes pelo botão de fechar da lista.
          </p>
          {erro && <div className="form-error">{erro}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn-secondary" onClick={onClose} disabled={enviando}>
            Cancelar
          </button>
          <button className="btn-primary" onClick={enviar} disabled={enviando}>
            {enviando ? "Publicando…" : "Publicar aviso"}
          </button>
        </div>
      </div>
    </div>
  );
}
